/**
 * PenthouseSection — Via6 two-column Penthouse layout.
 *
 * Left column (matches the brand screenshot):
 *   1. Large brand heading ("Penthouse Living")
 *   2. Four-up image strip (4 image slot groups, each with image+alt)
 *   3. Sub-heading title ("Penthouse Interior Features")
 *   4. Richtext list of penthouse features
 *   5. CTA button → "Explore Our Amenities" (defaults to /amenities)
 *
 * Right column:
 *   - A static <div class="rrac_pent_layout" data-group="penthouse"> that
 *     the site-global apartment-sync-loader (Repli360 / RRAC) script
 *     hydrates at runtime: it scans for `.rrac_pent_layout`, reads
 *     `data-group`, and POSTs to repli360's template-render endpoint,
 *     then `.html()`s the floorplans carousel response into the div.
 *     No editable block — the script owns the rendered output.
 *
 *     The container intentionally has no height cap, overflow clip, or
 *     extra wrapper. The injected widget (Owl Carousel) measures its
 *     parent's width to compute slide widths, so any `overflow-*`,
 *     `max-height`, or scroll container on an ancestor breaks the
 *     carousel layout. The grid's `items-start` lets the right column
 *     render at the carousel's natural height independent of the left
 *     column — same as the live site at via6living.com/penthouse.
 *
 * Background = theme primary (the surrounding floor mirrors the page
 * hero's deep navy in the brand screenshot); copy = text-on-primary.
 */

import { useEffect } from 'react';
import type {
  ComponentContentSchema,
  SlotGroupDefinition,
} from '@vibesites/runtime';
import {
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ---------------------------------------------------------------------------
// Slot group definitions
// ---------------------------------------------------------------------------

// CTA — a standard link slot group (url + label + _target). Defaults push
// the visitor over to the Amenities page so the section reads like the
// brand screenshot's "EXPLORE OUR AMENITIES" pill.
const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/amenities',
      suggestedFields: [
        'property_apply_now_url',
        'property_website_url',
      ],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Explore Our Amenities',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// Image slot group definitions for the 4-up strip. Each gets its own
// `defaultIndex` into the property photo gallery so a freshly-deployed
// site has 4 distinct photos out of the box. Editors can re-pick each
// slot independently.
const image1Def: SlotGroupDefinition = {
  label: 'Feature Image 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Penthouse interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Feature Image 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Penthouse interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Feature Image 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Penthouse interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image4Def: SlotGroupDefinition = {
  label: 'Feature Image 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      defaultValue: 'Penthouse interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PenthouseSection() {
  const image1 = useSlotGroup('penthousesection.image_1', image1Def);
  const image2 = useSlotGroup('penthousesection.image_2', image2Def);
  const image3 = useSlotGroup('penthousesection.image_3', image3Def);
  const image4 = useSlotGroup('penthousesection.image_4', image4Def);
  const cta = useSlotGroup('penthousesection.cta', ctaLinkDef);

  // Lazy-load the "Satisfy" cursive Google Font used by the
  // .vibe-cursive-accent span when the heading default (or an editor
  // override) wraps a word in the accent treatment. De-duped against
  // sibling components by the data-vibe-font marker so the stylesheet
  // link is injected at most once per page.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      <style>{`
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }
      `}</style>

      <div className="max-w-7xl xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* ============================================================
              LEFT COLUMN — heading, 4-up image strip, sub-title,
              richtext list, CTA button. */}
          <div className="text-[var(--vibe-color-text-on-primary)]">
            {/* Heading row — secondary-color vertical accent bar on the
                left of the brand heading, matching the screenshot. The
                bar is a sibling div (rather than a `border-left` on the
                heading itself) so it stays a fixed thickness regardless
                of the heading's line-height changes, and so it spans the
                full height of the multi-line heading via `self-stretch`
                on a flex row. `items-stretch` on the flex container
                pins the bar to the top + bottom of the longest column
                (the heading) automatically. */}
            <div className="flex items-stretch gap-5 sm:gap-6">
              <div
                aria-hidden="true"
                className="w-1 sm:w-1.5 bg-[var(--vibe-color-secondary)] flex-shrink-0 self-stretch"
              />
              <RichTextContent
                path="penthousesection.heading"
                as="h2"
                className="flex-1 font-heading text-4xl sm:text-5xl md:text-6xl font-normal leading-tight uppercase tracking-wide [&_strong]:font-normal [&_b]:font-normal"
              />
            </div>

            {/* 4-up image strip — each tile is a square thumbnail.
                Grid stays 4 across at every breakpoint so it reads as
                the row in the brand screenshot, with smaller gaps on
                phones to keep the tiles a usable size.

                Each tile is its own literal-path EditableSlotGroup so
                the schema linter can statically match the wrapper to
                each `penthousesection.image_*` slot group declaration.
                A .map() loop with dynamic groupPath= would render the
                same DOM but the linter can't see dynamic paths. */}
            <div className="mt-8 grid grid-cols-4 gap-2 sm:gap-3">
              <EditableSlotGroup
                groupPath="penthousesection.image_1"
                definition={image1Def}
              >
                <div className="w-full aspect-square overflow-hidden">
                  {image1.image ? (
                    <VibesitesImage
                      url={image1.image}
                      alt={image1.alt || 'Penthouse interior'}
                      className="block w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[var(--vibe-color-text)]/20" />
                  )}
                </div>
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="penthousesection.image_2"
                definition={image2Def}
              >
                <div className="w-full aspect-square overflow-hidden">
                  {image2.image ? (
                    <VibesitesImage
                      url={image2.image}
                      alt={image2.alt || 'Penthouse interior'}
                      className="block w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[var(--vibe-color-text)]/20" />
                  )}
                </div>
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="penthousesection.image_3"
                definition={image3Def}
              >
                <div className="w-full aspect-square overflow-hidden">
                  {image3.image ? (
                    <VibesitesImage
                      url={image3.image}
                      alt={image3.alt || 'Penthouse interior'}
                      className="block w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[var(--vibe-color-text)]/20" />
                  )}
                </div>
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="penthousesection.image_4"
                definition={image4Def}
              >
                <div className="w-full aspect-square overflow-hidden">
                  {image4.image ? (
                    <VibesitesImage
                      url={image4.image}
                      alt={image4.alt || 'Penthouse interior'}
                      className="block w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[var(--vibe-color-text)]/20" />
                  )}
                </div>
              </EditableSlotGroup>
            </div>

            {/* Sub-heading title above the feature list */}
            <RichTextContent
              path="penthousesection.subtitle"
              as="h3"
              className="mt-10 font-body text-base sm:text-lg font-semibold tracking-[0.2em] uppercase text-center"
            />

            {/* Feature list (richtext) */}
            <RichTextContent
              path="penthousesection.description"
              as="div"
              className="mt-6 font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:text-[var(--vibe-color-text-on-primary)] [&_a]:underline [&_strong]:font-semibold [&_p]:mt-3 first:[&_p]:mt-0 [&_ul]:mt-3 [&_ul]:space-y-2 [&_li]:list-none"
            />

            {/* CTA button — pill matching the rest of the Via6 theme. */}
            <div className="mt-10 flex justify-center">
              <EditableSlotGroup
                groupPath="penthousesection.cta"
                definition={ctaLinkDef}
              >
                <a
                  href={cta.url || '/amenities'}
                  target={cta._target || '_self'}
                  className="inline-block text-center px-10 py-3 text-sm font-medium tracking-[0.2em] uppercase rounded-full bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-secondary)] hover:bg-transparent hover:border-[var(--vibe-color-text-on-primary)] transition-all"
                >
                  {cta.label || 'Explore Our Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* ============================================================
              RIGHT COLUMN — empty <div> hydrated at runtime by the
              site-global apartment-sync-loader (Repli360 / RRAC) script.
              The script jQuery-selects `.rrac_pent_layout`, reads
              `data-group` ("penthouse"), POSTs to repli360's
              template-render endpoint, and injects the floorplans
              carousel HTML into the div via `.html(data)`. No
              useBlockSlot / editor block here — the script owns the
              rendered output.

              Matches the markup at via6living.com/penthouse exactly:
              a plain column wrapper, no height cap or overflow clip
              (Owl Carousel measures parent width to compute slide
              widths — wrapping it in a scroll container breaks the
              layout). The grid's `items-start` lets this column take
              its natural carousel height independent of the left
              column. */}
          <div>
            <div
              className="rrac_pent_layout"
              data-group="penthouse"
              style={{ backgroundColor: '#fff' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export const contentSchema: ComponentContentSchema = {
  slot: 'PenthouseSection',
  displayName: 'Penthouse Section',
  tokens: {
    'penthousesection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span class="vibe-cursive-accent">Via6</span><br>Penthouse Living</p>',
    },
    'penthousesection.subtitle': {
      label: 'Sub-heading',
      type: 'richtext',
      defaultValue: '<p>Penthouse Interior Features</p>',
    },
    'penthousesection.description': {
      label: 'Feature List',
      type: 'richtext',
      defaultValue:
        '<ul><li>Gourmet kitchens w/energy-efficient appliances</li><li>Views of the City and Puget Sound</li><li>Air conditioning*</li><li>Walk-in closets*</li><li>Stone countertops in kitchens and bath spaces</li><li>Ceramic stovetop ranges</li></ul>',
    },
  },
  slotGroups: {
    'penthousesection.image_1': image1Def,
    'penthousesection.image_2': image2Def,
    'penthousesection.image_3': image3Def,
    'penthousesection.image_4': image4Def,
    'penthousesection.cta': ctaLinkDef,
  },
};

(PenthouseSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PenthouseSection;
